// Erkennt Wiederbewerber: dieselbe Person, die sich schon einmal beworben hat.
//
// Der Name ist der Anker. Er ist die einzige Angabe, die jede Bewerbung
// strukturiert trägt (system_name -> submissions.applicant_name) und deshalb
// auch für Altbestände verfügbar. E-Mail und Telefon liegen nur im
// answers-JSON und sind je Fragebogen optional — sie bestätigen einen
// Namenstreffer, ersetzen ihn aber nie.

import { flattenAnswerText, extractContactValues } from './contactInfo';

export const MATCH_NAME_ONLY = 'name';
export const MATCH_PHONE = 'phone';
export const MATCH_EMAIL = 'email';

const UMLAUTS = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

// Ein Schlüssel je Person. Sortierte Namensteile, damit "Max Müller" und
// "Müller, Max" zusammenfallen; deutsche Umlaute werden ausgeschrieben, damit
// "Müller" und "Mueller" es ebenfalls tun, danach fallen die übrigen
// Diakritika (é, ç, ł) auf ihren Grundbuchstaben.
export const normalizeName = (raw) => {
    const lower = String(raw || '').toLowerCase();
    const spelled = lower.replace(/[äöüß]/g, (char) => UMLAUTS[char]);
    const ascii = spelled.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return ascii.split(/[^a-z0-9]+/).filter(Boolean).sort().join(' ');
};

// Platzhalter, die das Formular schreibt, wenn kein Name angegeben wurde.
// Ohne diesen Filter wären sie die größten Treffergruppen überhaupt.
const PLACEHOLDER_KEYS = new Set(
    ['Unbekannt', 'Not provided', 'Keine Angabe', 'Anonym'].map(normalizeName)
);

// Ein einzelner Vorname ist als Merkmal zu schwach — alle "Michael" wären
// sonst dieselbe Person. Erst ab zwei Namensteilen wird verglichen.
export const isUsableName = (raw) => {
    const key = normalizeName(raw);
    if (!key || PLACEHOLDER_KEYS.has(key)) return false;
    return key.split(' ').length >= 2;
};

export const normalizeEmail = (raw) => {
    const value = String(raw || '').trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : '';
};

// Deutsche Schreibweisen auf eine Form bringen: +49 170…, 0049170… und 0170…
// sind dieselbe Nummer. Ausländische Vorwahlen bleiben unangetastet — Haupt-
// sache, dieselbe Eingabe ergibt immer denselben Schlüssel.
export const normalizePhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length < 7) return '';
    let national = digits.startsWith('00') ? digits.slice(2) : digits;
    if (national.startsWith('49')) national = national.slice(2);
    return national.replace(/^0+/, '');
};

const unique = (values) => [...new Set(values.filter(Boolean))];

// Sucht E-Mail und Telefon in allen Antworten. Bewusst über den ganzen Text
// statt über die Frage: der Wortlaut der Kontaktfrage wechselt je Fragebogen,
// und die Listenansicht hat die Fragen ohnehin nicht geladen.
export const contactKeysFromAnswers = (answers) => {
    if (!answers || typeof answers !== 'object') return { emails: [], phones: [] };
    const { emails, phones } = extractContactValues(flattenAnswerText(answers));
    return {
        emails: unique(emails.map(normalizeEmail)),
        phones: unique(phones.map(normalizePhone))
    };
};

// Map von Namensschlüssel auf alle Bewerbungen dieser Person. Wird einmal über
// den Gesamtbestand gebaut, nicht über die gefilterte Liste — sonst
// verschwände ein Treffer, sobald nach Status oder Fragebogen gefiltert wird.
export const buildNameIndex = (submissions = []) => {
    const index = new Map();
    submissions.forEach((submission) => {
        if (!submission || !isUsableName(submission.applicantName)) return;
        const key = normalizeName(submission.applicantName);
        if (!index.has(key)) index.set(key, []);
        index.get(key).push(submission);
    });
    return index;
};

const submittedTime = (submission) => {
    const time = new Date(submission?.submittedAt).getTime();
    return Number.isNaN(time) ? 0 : time;
};

// Die übrigen Bewerbungen derselben Person, neueste zuerst.
export const findRelatedApplications = (submission, index) => {
    if (!submission || !isUsableName(submission.applicantName)) return [];
    const group = index?.get(normalizeName(submission.applicantName)) || [];
    return group
        .filter((other) => other.id !== submission.id)
        .sort((a, b) => submittedTime(b) - submittedTime(a));
};

const overlaps = (a = [], b = []) => a.some((value) => b.includes(value));

// Wie sicher ist der Treffer? Die E-Mail steht über der Telefonnummer, weil
// sie seltener geteilt wird als ein Festnetzanschluss.
export const classifyMatch = (current, other) => {
    if (overlaps(current?.emails, other?.emails)) return MATCH_EMAIL;
    if (overlaps(current?.phones, other?.phones)) return MATCH_PHONE;
    return MATCH_NAME_ONLY;
};

export const MATCH_LABELS = {
    [MATCH_EMAIL]: 'Name und E-Mail identisch',
    [MATCH_PHONE]: 'Name und Telefonnummer identisch',
    [MATCH_NAME_ONLY]: 'Nur Namensgleichheit'
};
